// # User Support Component
import React, { useState, useEffect } from 'react';
import DashboardHeader from '../../components/DashboardHeader';
import DashboardFooter from '../../components/DashboardFooter';
import { 
  FaHeadset, 
  FaTimes 
} from 'react-icons/fa';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../../auth/firebase';
import { useAuthStore } from '../../auth/state';
import cacheManager from '../../auth/cache_manager';
import './user_support.css';

const UserSupport = () => {
  // # State for dialog visibility and form data
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [supportType, setSupportType] = useState('technical');
  const [message, setMessage] = useState('');
  const { user } = useAuthStore();
  const [isDarkMode, setIsDarkMode] = useState(false);

  // # Load theme preference from user data on initial render
  useEffect(() => {
    const loadThemePreference = async () => {
      if (user && user.uid) {
        // # Try to get from cache first
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData && userData.themes && userData.themes.gen_theme !== undefined) {
          setIsDarkMode(userData.themes.gen_theme === 'dark');
        }
      }
    };
    
    loadThemePreference();
  }, [user]);

  // # Handle theme toggle
  const handleThemeToggle = async () => {
    try {
      const newTheme = !isDarkMode;
      setIsDarkMode(newTheme);
      
      // # Update theme in Firebase if user is logged in
      if (user && user.uid) {
        // # Update the user document
        await updateDoc(doc(db, 'users', user.uid), {
          'themes.gen_theme': newTheme ? 'dark' : 'light'
        });
        
        // # Update cache manager
        const userData = cacheManager.get(`user_${user.uid}`);
        if (userData) {
          const updatedUserData = {
            ...userData,
            themes: {
              ...(userData.themes || {}),
              gen_theme: newTheme ? 'dark' : 'light'
            }
          };
          cacheManager.set(`user_${user.uid}`, updatedUserData);
        }

        // # Dispatch theme change event for footer and other components
        const themeChangeEvent = new CustomEvent('theme_change', {
          detail: { isDark: newTheme }
        });
        window.dispatchEvent(themeChangeEvent);
      }
    } catch (error) {
      console.error('Error updating theme preference:', error);
    }
  };

  // # Handle dialog open/close
  const handleOpenDialog = () => setIsDialogOpen(true);
  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSupportType('technical');
    setMessage('');
  };

  // # Handle form submission
  const handleSubmit = (e) => {
    e.preventDefault();
    // # TODO: Implement support request submission
    console.log('Support request:', { supportType, message });
    handleCloseDialog();
  };

  return (
    <div className={`usr_sprt_page-container ${isDarkMode ? 'usr_sprt_dark-mode' : ''}`}>
      <DashboardHeader isDarkMode={isDarkMode} onThemeToggle={handleThemeToggle} />
      
      <main className="usr_sprt_main-content">
        <div className="usr_sprt_header">
          <h1>My Support Requests</h1>
          <button 
            className="usr_sprt_new-request-btn"
            onClick={handleOpenDialog}
          >
            <FaHeadset className="usr_sprt_icon" />
            Ask for Support
          </button>
        </div>

        {/* # Support Dialog */}
        {isDialogOpen && (
          <div className="usr_sprt_dialog-overlay">
            <div className="usr_sprt_dialog">
              <div className="usr_sprt_dialog-header">
                <h2>New Support Request</h2>
                <button 
                  className="usr_sprt_close-btn"
                  onClick={handleCloseDialog}
                >
                  <FaTimes />
                </button>
              </div>

              <form onSubmit={handleSubmit} className="usr_sprt_form">
                <div className="usr_sprt_form-group">
                  <label htmlFor="supportType">Request Type</label>
                  <select
                    id="supportType"
                    value={supportType}
                    onChange={(e) => setSupportType(e.target.value)}
                    className="usr_sprt_select"
                  >
                    <option value="technical">Technical Issues</option>
                    <option value="subscription">Subscription Inquiries</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                <div className="usr_sprt_form-group">
                  <label htmlFor="message">Message</label>
                  <textarea
                    id="message"
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    className="usr_sprt_textarea"
                    placeholder="Please describe your issue..."
                    rows={5}
                  />
                </div>

                <div className="usr_sprt_button-group">
                  <button 
                    type="button" 
                    className="usr_sprt_cancel-btn"
                    onClick={handleCloseDialog}
                  >
                    Cancel
                  </button>
                  <button 
                    type="submit" 
                    className="usr_sprt_submit-btn"
                  >
                    Submit
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        <div className="usr_sprt_content">
          <div className="usr_sprt_empty-state">
            <FaHeadset className="usr_sprt_empty-icon" />
            <h2>No Support Requests</h2>
            <p>You haven't made any support requests yet.</p>
          </div>
        </div>
      </main>

      <DashboardFooter isDarkMode={isDarkMode} />
    </div>
  );
};

export default UserSupport;
